import React, { useState, useEffect } from 'react';
import { userAPI, networkAPI, leaveAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [subdists, setSubdists] = useState([]);
  const [filter, setFilter] = useState({ role: '', is_active: '' });
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', role: '', designation: '', area_district: '', area_zone: '', area_name: '', subdist_id: '' });
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', designation: '', area_district: '', area_zone: '', area_name: '', subdist_id: '', is_active: true, new_password: '', role: '', can_create_ticket: false });
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [leaveModal, setLeaveModal] = useState(null);
  const [leaveBackupTech, setLeaveBackupTech] = useState('');
  const [techLCOs, setTechLCOs] = useState([]);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const { tenant, user } = useAuth();
  const primaryColor = tenant ? tenant.branding.primary_color || '#1B6B3A' : '#1B6B3A';
  const canDelete = user && (user.role === 'superadmin' || user.role === 'isp_admin' || user.role === 'dist_head' || user.role === 'subdist_admin');
  const techRoles = ['subdist_tech_own', 'subdist_tech_isp', 'isp_technician', 'dist_l1', 'dist_l2', 'dist_l3'];

  const roles = [
    { value: 'isp_admin', label: 'ISP Admin' },
    { value: 'isp_technician', label: 'ISP Technician' },
    { value: 'dist_head', label: 'Distribution Head' },
    { value: 'dist_l3', label: 'Distribution L3' },
    { value: 'dist_l2', label: 'Distribution L2' },
    { value: 'dist_l1', label: 'Distribution L1' },
    { value: 'subdist_admin', label: 'Sub Dist Admin' },
    { value: 'subdist_tech_isp', label: 'Sub Dist Tech (ISP)' },
    { value: 'subdist_tech_own', label: 'Sub Dist Tech (Own)' },
    { value: 'lco', label: 'LCO' },
    { value: 'director', label: 'Director (View Only)' },
    { value: 'crm_staff', label: 'CRM Staff (Retention/Welcome)' },
    { value: 'end_user', label: 'End User' },
    { value: 'network_monitor', label: 'Network Monitor' },
    { value: 'isp_dist_head', label: 'ISP Distribution Head' },
    { value: 'area_engineer', label: 'Area Engineer' }
  ];

  const getRoleLabel = (role) => {
    const found = roles.find(r => r.value === role);
    return found ? found.label : role;
  };

  const subdistRoles = ['subdist_tech_isp', 'subdist_tech_own', 'subdist_admin', 'dist_l1', 'dist_l2', 'dist_l3', 'dist_head'];
  const noSubdistRoles = ['crm_staff', 'director', 'lco', 'end_user', 'network_monitor'];

  const loadUsers = () => {
    setLoading(true);
    userAPI.getAll(filter).then(res => {
      setUsers(res.data.users);
      setTotal(res.data.total);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUsers();
    networkAPI.getSubDistributions().then(res => {
      setSubdists(res.data.subdists || []);
    }).catch(() => {});
  }, [filter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');
    setSuccess('');
    try {
      const selectedSubdist = subdists.find(s => s._id === form.subdist_id);
      await userAPI.create({
        name: form.name, email: form.email, phone: form.phone,
        password: form.password, role: form.role,
        designation: form.designation,
        subdist_id: form.subdist_id || null,
        area: {
          district: selectedSubdist ? selectedSubdist.area.district : form.area_district,
          zone: selectedSubdist ? selectedSubdist.area.zone : form.area_zone,
          area_name: selectedSubdist ? selectedSubdist.area.area_name : form.area_name
        }
      });
      setSuccess('User created successfully!');
      setShowForm(false);
      setForm({ name: '', email: '', phone: '', password: '', role: '', designation: '', area_district: '', area_zone: '', area_name: '', subdist_id: '' });
      loadUsers();
    } catch (err) {
      setError(err.response ? err.response.data.message : 'Failed to create user');
    } finally { setFormLoading(false); }
  };

  const handleEditOpen = (u) => {
    setEditUser(u);
    setEditForm({
      name: u.name, phone: u.phone,
      designation: u.profile ? u.profile.designation : '',
      area_district: u.area ? u.area.district : '',
      area_zone: u.area ? u.area.zone : '',
      area_name: u.area ? u.area.area_name : '',
      subdist_id: u.subdist_id || '', is_active: u.is_active, new_password: '', role: u.role, email: u.email, can_create_ticket: u.can_create_ticket || false
    });
    setSuccess(''); setError('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');
    try {
      await userAPI.update(editUser._id, {
        name: editForm.name, email: editForm.email, phone: editForm.phone,
        designation: editForm.designation,
        is_active: editForm.is_active,
        can_create_ticket: editForm.can_create_ticket,
        role: editForm.role || editUser.role,
        area: { district: editForm.area_district, zone: editForm.area_zone, area_name: editForm.area_name },
        subdist_id: editForm.subdist_id || null,
      });
      if (editForm.new_password && editForm.new_password.trim() !== '') {
        await userAPI.resetPassword(editUser._id, { new_password: editForm.new_password });
      }
      setSuccess('User updated successfully!');
      setEditUser(null);
      loadUsers();
    } catch (err) {
      setError(err.response ? err.response.data.message : 'Failed to update');
    } finally { setFormLoading(false); }
  };

  const handleToggle = async (userId, currentStatus) => {
    try {
      await userAPI.update(userId, { is_active: !currentStatus });
      loadUsers();
    } catch (err) { alert('Failed to update user'); }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm('Delete user "' + name + '"? This cannot be undone!')) return;
    try {
      await userAPI.delete(userId);
      setSuccess('User deleted!');
      loadUsers();
    } catch (err) {
      setError(err.response ? err.response.data.message : 'Failed to delete');
    }
  };

  const handleLeaveOpen = async (u) => {
    setLeaveModal(u);
    setLeaveBackupTech('');
    try {
      const res = await leaveAPI.getTechLCOs(u._id);
      setTechLCOs(res.data.lcos || []);
    } catch (err) {
      setTechLCOs([]);
    }
  };

  const handleMarkOnLeave = async () => {
    if (!leaveBackupTech) return alert('Please select a backup technician');
    setLeaveLoading(true);
    try {
      const res = await leaveAPI.markOnLeave(leaveModal._id, { backup_technician_id: leaveBackupTech });
      setSuccess(res.data.message);
      setLeaveModal(null);
      loadUsers();
    } catch (err) {
      setError(err.response ? err.response.data.message : 'Failed to mark on leave');
    } finally { setLeaveLoading(false); }
  };

  const handleReturnFromLeave = async (u) => {
    if (!window.confirm(u.name + ' is returning from leave. All LCOs will be restored to them. Continue?')) return;
    setLeaveLoading(true);
    try {
      const res = await leaveAPI.returnFromLeave(u._id);
      setSuccess(res.data.message);
      loadUsers();
    } catch (err) {
      setError(err.response ? err.response.data.message : 'Failed to return from leave');
    } finally { setLeaveLoading(false); }
  };

  const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', marginBottom: '6px', color: '#333', fontSize: '14px', fontWeight: '500' };
  const roleColor = { superadmin: '#9c27b0', isp_admin: '#2196f3', isp_technician: '#03a9f4', dist_head: '#ff5722', dist_l3: '#ff9800', dist_l2: '#ffc107', dist_l1: '#ffeb3b', subdist_tech_isp: '#4caf50', subdist_tech_own: '#8bc34a', subdist_admin: '#00897b', director: '#5c6bc0', lco: '#009688', end_user: '#607d8b', network_monitor: '#795548', isp_dist_head: '#e91e63', area_engineer: '#009688', crm_staff: '#0891b2' };
  const availableTechs = users.filter(u => techRoles.includes(u.role) && u.is_active && (!leaveModal || u._id !== leaveModal._id) && !u.on_leave);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: '0', color: '#333' }}>User Management ({total})</h2>
        <button onClick={() => { setShowForm(!showForm); setEditUser(null); setSuccess(''); setError(''); }} style={{ background: primaryColor, color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
          {showForm ? 'Cancel' : '+ Add User'}
        </button>
      </div>
      {success && <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontWeight: '500' }}>{success}</div>}
      {error && <div style={{ background: '#ffebee', color: '#c62828', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>{error}</div>}

      {leaveModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', maxWidth: '500px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 8px', color: '#e65100' }}>🏖️ Mark On Leave — {leaveModal.name}</h3>
            <p style={{ margin: '0 0 16px', color: '#666', fontSize: '13px' }}>All LCOs and open tickets will be transferred to the selected backup technician</p>
            {techLCOs.length > 0 ? (
              <div style={{ background: '#fff3e0', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '600', color: '#e65100' }}>📍 {techLCOs.length} LCO(s) will be transferred:</p>
                {techLCOs.map(l => <p key={l._id} style={{ margin: '0 0 4px', fontSize: '13px', color: '#555' }}>• {l.name} ({l.area.area_name})</p>)}
              </div>
            ) : (
              <div style={{ background: '#e8f5e9', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                <p style={{ margin: '0', fontSize: '13px', color: '#2e7d32' }}>ℹ️ No LCOs assigned to this technician. Only open tickets will be transferred.</p>
              </div>
            )}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Select Backup Technician *</label>
              <select value={leaveBackupTech} onChange={e => setLeaveBackupTech(e.target.value)} style={inputStyle}>
                <option value="">-- Select Technician --</option>
                {availableTechs.map(t => <option key={t._id} value={t._id}>{t.name} ({getRoleLabel(t.role)}) — {t.phone}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={handleMarkOnLeave} disabled={leaveLoading || !leaveBackupTech} style={{ flex: 1, padding: '12px', background: leaveLoading || !leaveBackupTech ? '#ccc' : '#ff9800', color: 'white', border: 'none', borderRadius: '6px', cursor: leaveLoading || !leaveBackupTech ? 'not-allowed' : 'pointer', fontWeight: '600' }}>
                {leaveLoading ? 'Processing...' : '✅ Confirm Transfer & Mark On Leave'}
              </button>
              <button onClick={() => setLeaveModal(null)} style={{ padding: '12px 20px', background: 'white', color: '#333', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {editUser && (
        <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', padding: '24px', marginBottom: '20px', border: '2px solid #ff9800' }}>
          <h3 style={{ margin: '0 0 20px', color: '#e65100' }}>Edit User — {editUser.name}</h3>
          <div style={{ background: '#f5f5f5', padding: '10px 16px', borderRadius: '6px', marginBottom: '16px' }}>
            <p style={{ margin: '0', fontSize: '13px', color: '#666' }}><strong>Email:</strong> {editUser.email} &nbsp;&nbsp; <strong>Phone:</strong> {editUser.phone}</p>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Role *</label>
            <select value={editForm.role || editUser.role} onChange={e => setEditForm({...editForm, role: e.target.value})} style={inputStyle}>
              {roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <form onSubmit={handleEditSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div><label style={labelStyle}>Full Name *</label><input type="text" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} required style={inputStyle} /></div>
              <div><label style={labelStyle}>Phone</label><input type="text" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>Designation</label><input type="text" value={editForm.designation} onChange={e => setEditForm({...editForm, designation: e.target.value})} style={inputStyle} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div><label style={labelStyle}>Email</label><input type="email" value={editForm.email || ''} onChange={e => setEditForm({...editForm, email: e.target.value})} style={{...inputStyle, borderColor: '#2196f3'}} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div><label style={labelStyle}>District</label><input type="text" value={editForm.area_district} onChange={e => setEditForm({...editForm, area_district: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>Zone</label><input type="text" value={editForm.area_zone} onChange={e => setEditForm({...editForm, area_zone: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>Area Name</label><input type="text" value={editForm.area_name} onChange={e => setEditForm({...editForm, area_name: e.target.value})} style={inputStyle} /></div>
            </div>
            <div style={{ background: '#fff3e0', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 12px', color: '#e65100' }}>🔑 Reset Password (optional)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div><label style={labelStyle}>New Password</label><input type="text" placeholder="Leave blank to keep current password" value={editForm.new_password} onChange={e => setEditForm({...editForm, new_password: e.target.value})} style={inputStyle} /></div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <p style={{ margin: '0', fontSize: '12px', color: '#e65100' }}>⚠️ Enter new password only if you want to change it.</p>
                </div>
              </div>
            </div>
            {subdistRoles.includes(editUser.role) && (
              <div style={{ marginBottom: '16px', padding: '16px', background: '#e8f5e9', borderRadius: '8px' }}>
                <label style={labelStyle}>Sub Distribution</label>
                <select value={editForm.subdist_id} onChange={e => setEditForm({...editForm, subdist_id: e.target.value})} style={inputStyle}>
                  <option value="">Select Sub Distribution</option>
                  {subdists.map(s => <option key={s._id} value={s._id}>{s.name} ({s.code})</option>)}
                </select>
              </div>
            )}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={editForm.is_active} onChange={e => setEditForm({...editForm, is_active: e.target.checked})} style={{ width: '18px', height: '18px' }} />
                {editForm.role === 'crm_staff' && (
                  <>
                  <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" id="can_create_ticket" checked={editForm.can_create_ticket} onChange={e => setEditForm({...editForm, can_create_ticket: e.target.checked})} style={{ width: '18px', height: '18px' }} />
                    <label htmlFor="can_create_ticket" style={{ fontSize: '14px', color: '#374151', cursor: 'pointer' }}>Allow Ticket Creation</label>
                  </div>
                  <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" id="can_log_support" checked={editForm.can_log_support||false} onChange={e => setEditForm({...editForm, can_log_support: e.target.checked})} style={{ width: '18px', height: '18px' }} />
                    <label htmlFor="can_log_support" style={{ fontSize: '14px', color: '#374151', cursor: 'pointer' }}>Allow Support Call Logging</label>
                  </div>
                  </>
                )}
                <span style={{ fontWeight: '600', color: '#333' }}>Active</span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="submit" disabled={formLoading} style={{ padding: '10px 24px', background: formLoading ? '#ccc' : '#ff9800', color: 'white', border: 'none', borderRadius: '6px', cursor: formLoading ? 'not-allowed' : 'pointer', fontWeight: '600' }}>{formLoading ? 'Saving...' : 'Save Changes'}</button>
              <button type="button" onClick={() => setEditUser(null)} style={{ padding: '10px 24px', background: 'white', color: '#333', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {showForm && (
        <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', padding: '24px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 20px', color: primaryColor }}>Create New User</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div><label style={labelStyle}>Full Name *</label><input type="text" placeholder="Full name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required style={inputStyle} /></div>
              <div><label style={labelStyle}>Email *</label><input type="email" placeholder="Email address" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required style={inputStyle} /></div>
              <div><label style={labelStyle}>Phone *</label><input type="text" placeholder="Mobile number" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required style={inputStyle} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div><label style={labelStyle}>Password *</label><input type="password" placeholder="Set password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required style={inputStyle} /></div>
              <div>
                <label style={labelStyle}>Role *</label>
                <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} required style={inputStyle}>
                  <option value="">Select role</option>
                  {roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Designation</label><input type="text" placeholder="e.g. Field Technician" value={form.designation} onChange={e => setForm({...form, designation: e.target.value})} style={inputStyle} /></div>
            </div>
            {(subdistRoles.includes(form.role) || user.role === 'dist_head') && !noSubdistRoles.includes(form.role) && subdists.length > 0 && (
              <div style={{ marginBottom: '16px', padding: '16px', background: '#e8f5e9', borderRadius: '8px' }}>
                <label style={labelStyle}>Assign to Sub Distribution</label>
                <select value={form.subdist_id} onChange={e => setForm({...form, subdist_id: e.target.value})} style={inputStyle}>
                  <option value="">Select Sub Distribution</option>
                  {subdists.map(s => <option key={s._id} value={s._id}>{s.name} ({s.code}) — {s.area.area_name}</option>)}
                </select>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div><label style={labelStyle}>District</label><input type="text" placeholder="e.g. Kannur" value={form.area_district} onChange={e => setForm({...form, area_district: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>Zone</label><input type="text" placeholder="e.g. Zone 1" value={form.area_zone} onChange={e => setForm({...form, area_zone: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>Area Name</label><input type="text" placeholder="e.g. Payyanur" value={form.area_name} onChange={e => setForm({...form, area_name: e.target.value})} style={inputStyle} /></div>
            </div>
            <button type="submit" disabled={formLoading} style={{ padding: '12px 32px', background: formLoading ? '#ccc' : primaryColor, color: 'white', border: 'none', borderRadius: '6px', fontSize: '15px', fontWeight: '600', cursor: formLoading ? 'not-allowed' : 'pointer' }}>
              {formLoading ? 'Creating...' : 'Create User'}
            </button>
          </form>
        </div>
      )}

      <div style={{ background: 'white', padding: '16px', borderRadius: '10px', marginBottom: '16px', display: 'flex', gap: '12px' }}>
        <select value={filter.role} onChange={e => setFilter({...filter, role: e.target.value})} style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}>
          <option value="">All Roles</option>
          {roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select value={filter.is_active} onChange={e => setFilter({...filter, is_active: e.target.value})} style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}>
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <button onClick={() => setFilter({ role: '', is_active: '' })} style={{ padding: '8px 16px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer' }}>Clear</button>
      </div>

      <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {loading ? <p style={{ padding: '20px', textAlign: 'center' }}>Loading...</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: primaryColor }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: 'white' }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: 'white' }}>Email</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: 'white' }}>Phone</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: 'white' }}>Role</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: 'white' }}>Area</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: 'white' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: 'white' }}>Last Login</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', color: 'white' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan="8" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No users found</td></tr>
              ) : users.map(function(u) {
                const isTech = techRoles.includes(u.role);
                return (
                  <tr key={u._id} style={{ borderBottom: '1px solid #f0f0f0', background: u.on_leave ? '#fff8e1' : 'white' }}>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontWeight: '600', fontSize: '13px' }}>{u.name}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>{u.profile ? u.profile.designation : ''}</div>
                      {u.on_leave && <div style={{ fontSize: '11px', background: '#ff980020', color: '#e65100', padding: '2px 6px', borderRadius: '4px', marginTop: '4px', display: 'inline-block' }}>🏖️ On Leave</div>}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px' }}>{u.email}</td>
                    <td style={{ padding: '12px', fontSize: '13px' }}>{u.phone}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ background: (roleColor[u.role] || '#607d8b') + '20', color: roleColor[u.role] || '#607d8b', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>
                        {getRoleLabel(u.role)}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#666' }}>{u.area && u.area.area_name ? u.area.area_name : '-'}</td>
                    <td style={{ padding: '12px' }}><span style={{ background: u.is_active ? '#4caf5020' : '#f4433620', color: u.is_active ? '#4caf50' : '#f44336', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#666' }}>{u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}</td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button onClick={() => handleEditOpen(u)} style={{ background: '#ff980020', color: '#e65100', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Edit</button>
                        <button onClick={() => handleToggle(u._id, u.is_active)} style={{ background: u.is_active ? '#f4433620' : '#4caf5020', color: u.is_active ? '#f44336' : '#4caf50', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        {isTech && !u.on_leave && <button onClick={() => handleLeaveOpen(u)} style={{ background: '#ff980020', color: '#e65100', border: '1px solid #ff9800', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>🏖️ On Leave</button>}
                        {isTech && u.on_leave && <button onClick={() => handleReturnFromLeave(u)} disabled={leaveLoading} style={{ background: '#4caf5020', color: '#4caf50', border: '1px solid #4caf50', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>✅ Return</button>}
                        {canDelete && <button onClick={() => handleDelete(u._id, u.name)} style={{ background: '#ffebee', color: '#c62828', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Delete</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Users;